import { Icon } from '@ps/isp-web-component-library';
import { ChevronSmallRight } from '@ps/isp-web-component-library/icons/ChevronSmallRight';
import type {
  CompetitionLink,
  EventTypeLink,
  MeetingLink,
  RunnerLink,
  SearchEventLink
} from '@ps/isp-web-services-types';
import { type Alias, select } from '@ps/web-core-store';
import { Link } from '@ps/web-shared-components';
import { capitalize } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

import FormattedDateTime from '@swf/sports/components/FormattedDateTime//FormattedDateTime';
import { TitleWithHighlight } from '@swf/sports/components/TitleWithHighlight/TitleWithHighlight';

import { useSportsMenuWidget } from '../../../../model';
import { updatePreviousSearchResults } from '../PreviousSearchResults/utils/update-previous-search-results';

import styles from './SearchResult.scss';

interface Props {
  alias: Alias;
  link: EventTypeLink | RunnerLink | CompetitionLink | MeetingLink | SearchEventLink;
}

const shouldShowLinkDate = (
  link: Props['link']
): link is SearchEventLink | MeetingLink | RunnerLink => 'openDate' in link;

const SearchResult: React.FC<Props> = ({ alias, link }) => {
  const { widgetDispatch, widgetSelect } = useSportsMenuWidget();
  const searchQuery = useSelector(widgetSelect.searchQuery);
  const locale = useSelector(select.locale.get);
  let linkText: string = '';

  switch (true) {
    case 'text' in link:
      linkText = link.text;
      break;
    case 'runnerName' in link:
      linkText = link.runnerName;
      break;
    case 'venue' in link:
      linkText = link.venue;
      break;
    default:
      break;
  }

  const searchResultTextWithHighlight = (
    <TitleWithHighlight title={linkText} partToHighlight={searchQuery} />
  );

  const handleSearchResultClick = () => {
    const capitalizedQuery = capitalize(searchQuery);
    const updatedPrevSearchResults = updatePreviousSearchResults(capitalizedQuery);

    widgetDispatch.updatePreviousSearchResults(updatedPrevSearchResults);
    widgetDispatch.togglePreviousSearchResults(false);
    widgetDispatch.updateSearchQuery('');
  };

  return (
    <li className={styles.container}>
      <Link
        className={styles.searchResult}
        alias={alias}
        values={{ ...link }}
        onClick={handleSearchResultClick}
        trackText={locale === 'en' ? linkText : undefined}
      >
        <div className={styles.info}>
          {searchResultTextWithHighlight}
          {shouldShowLinkDate(link) ? (
            <span className={styles.secondaryInfo}>
              <FormattedDateTime date={link.openDate} format="long" minimal />{' '}
              {'competitionName' in link && `- ${link.competitionName}`}
              {'runnerName' in link && `- ${link.venue}`}
            </span>
          ) : null}
        </div>
        <Icon icon={ChevronSmallRight} size="lg" />
      </Link>
    </li>
  );
};

export default SearchResult;
